import { roundMoney } from './money-rounding';
import { isV13BreakSemantics, type BreakSemantics } from './payroll/break-treatment';
export type CanonicalPayrollAbsenceType =
  | 'ANNUAL'
  | 'PERSONAL'
  | 'CARERS_EXTRA_GENERAL_STAFF'
  | 'LONG_SERVICE'
  | 'COMPASSIONATE'
  | 'UNPAID'
  | 'PARENTAL'
  | 'COMMUNITY_SERVICE'
  | 'WORKERS_COMP_NOT_WORKED'
  | 'TIME_IN_LIEU_TAKEN'
  | 'ACCRUED_DAY_OFF_TAKEN'
  | 'PUBLIC_HOLIDAY';

export type LeavePayTreatmentMode =
  | 'ANNUAL_HIGHER_OF'
  | 'BASE_RATE'
  | 'TASMANIAN_ORDINARY_PAY'
  | 'ZERO_PAY'
  | 'EVIDENCE_BACKED_HANDOFF'
  | 'SEPARATE_PUBLIC_HOLIDAY_EVENT';

export interface LeavePayTreatmentDefinition {
  type: CanonicalPayrollAbsenceType;
  mode: LeavePayTreatmentMode;
  automatic: boolean;
  summary: string;
  evidenceRequired: string | null;
}

export const LEAVE_PAY_TREATMENTS: Record<
  CanonicalPayrollAbsenceType,
  LeavePayTreatmentDefinition
> = {
  ANNUAL: {
    type: 'ANNUAL',
    mode: 'ANNUAL_HIGHER_OF',
    automatic: true,
    summary: 'Ordinary hours plus the higher of 17.5% loading or projected roster penalties.',
    evidenceRequired: 'Exact leave segments and assigned roster shifts.',
  },
  PERSONAL: {
    type: 'PERSONAL',
    mode: 'BASE_RATE',
    automatic: true,
    summary: 'Base rate for ordinary rostered hours; no loadings, penalties or overtime.',
    evidenceRequired:
      'Exact leave segments; certificate/evidence rules remain an approval control.',
  },
  CARERS_EXTRA_GENERAL_STAFF: {
    type: 'CARERS_EXTRA_GENERAL_STAFF',
    mode: 'BASE_RATE',
    automatic: true,
    summary: "Base rate against Medea General Staff's separate non-cumulative carer's entitlement.",
    evidenceRequired: 'Exact leave segments and qualifying carer-leave approval evidence.',
  },
  LONG_SERVICE: {
    type: 'LONG_SERVICE',
    mode: 'TASMANIAN_ORDINARY_PAY',
    automatic: true,
    summary: 'Tasmanian ordinary pay, including evidenced ordinary shift penalties.',
    evidenceRequired: 'Exact leave segments and assigned roster shifts.',
  },
  COMPASSIONATE: {
    type: 'COMPASSIONATE',
    mode: 'BASE_RATE',
    automatic: true,
    summary: 'Base rate for ordinary hours the permanent employee would have worked.',
    evidenceRequired: 'Qualifying event and exact leave segments.',
  },
  UNPAID: {
    type: 'UNPAID',
    mode: 'ZERO_PAY',
    automatic: true,
    summary: 'Absence is roster-visible and produces no payroll earnings.',
    evidenceRequired: null,
  },
  PARENTAL: {
    type: 'PARENTAL',
    mode: 'EVIDENCE_BACKED_HANDOFF',
    automatic: false,
    summary:
      'Payment depends on unpaid, government-funded, employer-funded or no-safe-job subtype.',
    evidenceRequired: 'Confirmed subtype, dates and provider/payment evidence.',
  },
  COMMUNITY_SERVICE: {
    type: 'COMMUNITY_SERVICE',
    mode: 'EVIDENCE_BACKED_HANDOFF',
    automatic: false,
    summary: 'Emergency service is unpaid; jury duty may require first-10-day make-up pay.',
    evidenceRequired: 'Activity subtype, jury payment evidence and ordinary roster hours.',
  },
  WORKERS_COMP_NOT_WORKED: {
    type: 'WORKERS_COMP_NOT_WORKED',
    mode: 'EVIDENCE_BACKED_HANDOFF',
    automatic: false,
    summary: 'Tasmanian statutory weekly payment; not an ordinary leave-rate calculation.',
    evidenceRequired: 'Accepted claim/certificate and statutory weekly-rate calculation.',
  },
  TIME_IN_LIEU_TAKEN: {
    type: 'TIME_IN_LIEU_TAKEN',
    mode: 'EVIDENCE_BACKED_HANDOFF',
    automatic: false,
    summary: 'Paid only against a valid accrued TOIL liability and the applicable agreement rule.',
    evidenceRequired: 'Accrual source, remaining balance and approved hours taken.',
  },
  ACCRUED_DAY_OFF_TAKEN: {
    type: 'ACCRUED_DAY_OFF_TAKEN',
    mode: 'EVIDENCE_BACKED_HANDOFF',
    automatic: false,
    summary: 'Paid only against a valid accrued day off balance and the applicable agreement rule.',
    evidenceRequired: 'Accrual source, remaining balance and approved hours taken.',
  },
  PUBLIC_HOLIDAY: {
    type: 'PUBLIC_HOLIDAY',
    mode: 'SEPARATE_PUBLIC_HOLIDAY_EVENT',
    automatic: false,
    summary: 'Paid public-holiday-not-worked event, not a leave-balance deduction.',
    evidenceRequired: 'Rostered ordinary hours and jurisdiction/facility holiday match.',
  },
};

/**
 * THE canonical set of `leave_type` tokens meaning personal/carer's leave.
 *
 * Both the payroll normaliser below and the database filter derive from this one array, so
 * they cannot drift apart. They previously did: an earlier revision hand-wrote a separate
 * DB list containing `PERSONAL_LEAVE` while the normaliser's branch did NOT, so
 * `normalisePayrollAbsenceType('PERSONAL_LEAVE')` returned null and the pay-run snapshot
 * dropped those rows as `unknown_leave_type` (see `applyLeaveToPayRun`). Both external
 * auditors caught the two lists disagreeing.
 */
const PERSONAL_ABSENCE_TYPE_TOKENS = [
  'PERSONAL',
  'PERSONAL_GENERAL_STAFF',
  'PERSONAL_NURSES',
  'PERSONAL_LEAVE',
  'PERSONAL_CARERS',
  'PERSONAL_CARER',
  'PERSONAL_CARERS_LEAVE',
  'PERSONAL_CARER_LEAVE',
  'PERSONAL_SICK',
  'PERSONAL_SICK_LEAVE',
  'SICK',
  'SICK_LEAVE',
  'CARERS_LEAVE',
  'CARER_LEAVE',
  'CARER_S_LEAVE',
] as const;

/**
 * The same tokens in the casings a DATABASE filter must match.
 *
 * `normalisePayrollAbsenceType` upper-cases before matching, so the payroll path tolerates
 * any casing. A PostgREST filter cannot call it: `.eq('leave_type', 'SICK')` is exact and
 * case-sensitive, and it matched 6 of the 56 personal/sick rows on record — silently
 * dropping `PERSONAL` (43), `sick` (3), `PERSONAL_LEAVE` (3) and `personal` (1).
 *
 * Use with `.in('leave_type', [...])`. Extend `PERSONAL_ABSENCE_TYPE_TOKENS` — never
 * re-inline a token list at a call site.
 *
 * Covers UPPER and lower only. Every token in production is one of those two (measured
 * 2026-07-31 across all tenants); a title-case or space-separated literal would still miss,
 * so normalise on write rather than adding casings here.
 */
export const PERSONAL_LEAVE_TYPE_DB_TOKENS = PERSONAL_ABSENCE_TYPE_TOKENS.flatMap((token) => [
  token as string,
  token.toLowerCase(),
]);

export function normalisePayrollAbsenceType(value: string): CanonicalPayrollAbsenceType | null {
  const type = value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (type === 'ANNUAL' || type === 'ANNUAL_LEAVE') return 'ANNUAL';
  if ((PERSONAL_ABSENCE_TYPE_TOKENS as readonly string[]).includes(type)) {
    return 'PERSONAL';
  }
  if (
    [
      'CARERS_EXTRA_GENERAL_STAFF',
      'EXTRA_CARERS_GENERAL_STAFF',
      'GENERAL_STAFF_EXTRA_CARERS',
    ].includes(type)
  ) {
    return 'CARERS_EXTRA_GENERAL_STAFF';
  }
  if (type === 'LONG_SERVICE' || type === 'LONG_SERVICE_LEAVE' || type === 'LSL') {
    return 'LONG_SERVICE';
  }
  if (type === 'COMPASSIONATE' || type === 'COMPASSIONATE_LEAVE' || type === 'BEREAVEMENT') {
    return 'COMPASSIONATE';
  }
  if (type === 'UNPAID' || type === 'UNPAID_LEAVE' || type === 'LEAVE_WITHOUT_PAY') {
    return 'UNPAID';
  }
  if (type === 'PARENTAL' || type === 'PARENTAL_LEAVE') return 'PARENTAL';
  if (type === 'COMMUNITY_SERVICE' || type === 'COMMUNITY_SERVICE_LEAVE' || type === 'JURY_DUTY') {
    return 'COMMUNITY_SERVICE';
  }
  if (
    ['WORKERS_COMP_NOT_WORKED', 'WORKERS_COMPENSATION_NOT_WORKED', 'WORKCOVER_NOT_WORKED'].includes(
      type,
    )
  ) {
    return 'WORKERS_COMP_NOT_WORKED';
  }
  if (type === 'TIME_IN_LIEU_TAKEN' || type === 'TOIL_TAKEN' || type === 'TIL') {
    return 'TIME_IN_LIEU_TAKEN';
  }
  if (type === 'ACCRUED_DAY_OFF_TAKEN' || type === 'ACCRUED_DAY_OFF' || type === 'ADO_TAKEN') {
    return 'ACCRUED_DAY_OFF_TAKEN';
  }
  if (type === 'PUBLIC_HOLIDAY' || type === 'PUBLIC_HOLIDAY_NOT_WORKED') {
    return 'PUBLIC_HOLIDAY';
  }
  return null;
}

export function leavePayTreatmentFor(value: string): LeavePayTreatmentDefinition | null {
  const type = normalisePayrollAbsenceType(value);
  return type ? LEAVE_PAY_TREATMENTS[type] : null;
}

/** Mirrors `internal.leave_deductible_break_minutes` for TypeScript resolver parity. */
export function leaveDeductibleBreakMinutes(args: {
  breakMinutes: number;
  spanMinutes: number;
  countsForLeave: boolean;
  triggerAfterContinuousMinutes?: number | null;
  /** R71: omitted = LEGACY_28D1 (rebuild OFF), production 28d166931e. */
  breakSemantics?: BreakSemantics;
}): number {
  const breakMinutes = Math.max(0, Math.round(args.breakMinutes ?? 0));
  if (!isV13BreakSemantics(args.breakSemantics)) {
    // LEGACY_28D1 body (28d166931e leave-pay-treatment.ts).
    if (!args.countsForLeave) return breakMinutes;
    const legacyTrigger = args.triggerAfterContinuousMinutes ?? 0;
    if (legacyTrigger <= 0 || args.spanMinutes > legacyTrigger) return 0;
    return breakMinutes;
  }
  const trigger = args.triggerAfterContinuousMinutes ?? 0;
  if (trigger > 0 && args.spanMinutes <= trigger) return 0;
  return args.countsForLeave ? 0 : breakMinutes;
}

/** Leave-valuation hours for one roster span after cohort break treatment. */
export function rosteredLeaveHoursFromSpan(args: {
  breakMinutes: number;
  spanMinutes: number;
  countsForLeave: boolean;
  triggerAfterContinuousMinutes?: number | null;
  breakSemantics?: BreakSemantics;
}): number {
  const spanMinutes = Math.max(0, Math.round(args.spanMinutes));
  const deductible = leaveDeductibleBreakMinutes(args);
  return Math.max(0, Math.round(((spanMinutes - deductible) / 60) * 10_000) / 10_000);
}

/** Canonical timesheet hours: recorded span less recorded break, rounded to 2 dp. */
export function canonicalTimesheetHoursFromSpan(args: {
  spanMinutes: number;
  breakMinutes: number;
}): number {
  const spanMinutes = Math.max(0, Math.round(args.spanMinutes));
  const breakMinutes = Math.max(0, Math.round(args.breakMinutes));
  return Math.round((Math.max(0, spanMinutes - breakMinutes) / 60) * 100) / 100;
}

export interface RosteredLeaveDayPayPart {
  hours: number;
  basePay: number;
  penaltyEquivalent: number;
}

function roundLeaveMoney(value: number): number {
  return roundMoney(value);
}

/** Weekday ordinary base for one roster shift date (Sat/Sun band ordinary for leave). */
export function rosteredLeaveWeekdayBandOrdinaryBase(args: {
  shiftDate: string;
  weekdayOrdinaryBase: number;
}): number {
  const weekdayOrdinary = roundLeaveMoney(Math.max(0, args.weekdayOrdinaryBase));
  const day = new Date(`${args.shiftDate}T12:00:00Z`).getUTCDay();
  if (day === 6) return roundLeaveMoney(weekdayOrdinary * 1.5);
  if (day === 0) return roundLeaveMoney(weekdayOrdinary * 2);
  return weekdayOrdinary;
}

/**
 * Split one roster shift into weekday ordinary base (flat 17.5%) and the
 * band penalty-equivalent for that calendar day (Sat/Sun ordinary uplift).
 * Never treat full shift gross or engine penaltyPay as the leave premium.
 */
export function decomposeWorkedShiftLeavePay(args: {
  basePay: number;
  penaltyPay: number;
  shiftLoadingPay: number;
  grossTotal: number;
  weekdayOrdinaryBase: number;
  bandOrdinaryBase?: number | null;
}): { basePay: number; penaltyEquivalent: number } {
  const weekdayOrdinary = roundLeaveMoney(Math.max(0, args.weekdayOrdinaryBase));
  const bandOrdinary =
    args.bandOrdinaryBase != null && args.bandOrdinaryBase > 0
      ? roundLeaveMoney(args.bandOrdinaryBase)
      : weekdayOrdinary;
  return {
    basePay: weekdayOrdinary,
    penaltyEquivalent: roundLeaveMoney(Math.max(0, bandOrdinary - weekdayOrdinary)),
  };
}

export interface RosteredLeavePayDecomposition {
  basePay: number;
  penaltyEquivalent: number;
  hoursCompared: number;
}

/** Scale per-roster-day parts to the leave hours being paid in this period. */
export function aggregateRosteredLeavePayDecomposition(
  parts: readonly RosteredLeaveDayPayPart[],
  hoursToApply: number,
): RosteredLeavePayDecomposition {
  let totalBase = 0;
  let totalPenalty = 0;
  let totalHours = 0;
  for (const part of parts) {
    if (part.hours <= 0) continue;
    totalHours += part.hours;
    totalBase += part.basePay;
    // eslint-disable-next-line statura/no-floating-point-money -- summing cent-rounded per-day parts before a final roundLeaveMoney.
    totalPenalty += part.penaltyEquivalent;
  }
  if (totalHours <= 0) {
    return { basePay: 0, penaltyEquivalent: 0, hoursCompared: 0 };
  }
  const scale = Math.min(1, Math.max(0, hoursToApply) / totalHours);
  return {
    // eslint-disable-next-line statura/no-floating-point-money -- scale applies to cent-rounded per-day money; roundLeaveMoney canonicalises.
    basePay: roundLeaveMoney(totalBase * scale),
    // eslint-disable-next-line statura/no-floating-point-money -- ditto.
    penaltyEquivalent: roundLeaveMoney(totalPenalty * scale),
    hoursCompared: roundLeaveMoney(totalHours * scale),
  };
}
